import { Link } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  Package,
  FileText,
  BookOpen,
  TrendingDown,
  Users,
  Settings,
  MapPin,
  Shield,
  HelpCircle,
} from "lucide-react";

export default function UserGuidePage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to dashboard
        </Link>

        <article className="prose prose-neutral dark:prose-invert max-w-none prose-headings:font-display prose-p:text-muted-foreground prose-li:text-muted-foreground">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            ReviewBoost Pro Manager — User Guide
          </h1>
          <p className="text-sm text-muted-foreground">Last updated: March 2026</p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">Important notice</h2>
          <p className="rounded-lg border border-border bg-muted/50 p-4 text-sm">
            <strong>ReviewBoost Pro Manager is a software platform</strong> and is not a bank, payment processor, or
            financial institution. All financial transactions (subscriptions, payments) are conducted through Lenco and
            are subject solely to Lenco&apos;s regulatory compliance and licensing. For payment terms and pricing, see
            the{" "}
            <Link to="/terms" className="text-primary hover:underline">
              Terms of Service
            </Link>
            .
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">1. Overview</h2>
          <p>
            ReviewBoost Pro Manager is a Business Intelligence (BI) platform designed for Zambian retail and service
            businesses. It brings together your sales, inventory, and expenses to give you a clear health score for your
            business, smart AI tips, and monthly reports you can share with partners, lenders, and your team.
          </p>
          <p>
            As an <strong>Admin/Owner</strong>, you have full access to inventory, sales, expenses, reports, team
            management, and settings. Employees can be granted more limited access based on their role.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">2. Who this guide is for</h2>
          <ul>
            <li>
              <strong>Business owners and admins</strong> who want to track performance, manage multiple locations, and
              understand their monthly Business Health Score.
            </li>
            <li>
              <strong>Shop managers and staff</strong> who work with inventory, manual sales entry, and expenses.
            </li>
          </ul>
          <p>
            To use ReviewBoost Pro Manager, you need a modern web browser (for example, Chrome, Edge, or Safari), an
            internet connection, and a device such as a laptop, desktop, or recent smartphone. The app is responsive and
            works on mobile, but for detailed reports we recommend a larger screen.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">3. Getting started</h2>
          <h3 className="mt-4 text-base font-semibold text-foreground">Signing in</h3>
          <ul>
            <li>From the login page, sign in with your Google account.</li>
            <li>Your name and business context are used to personalise the dashboard.</li>
          </ul>
          <p>
            When you sign in, the dashboard shows a time-based greeting (good morning / afternoon / evening), the
            current date and time in Zambia, and your main KPIs and AI insights.
          </p>

          <h3 className="mt-4 text-base font-semibold text-foreground">Navigating the app</h3>
          <ul>
            <li>
              Use the <strong>sidebar</strong> to move between BI Analyst, Inventory, Reports, Manual Entry, Master
              Ledger, Expenses, Team, and Settings.
            </li>
            <li>
              Use the <strong>top bar</strong> to switch branches, open notifications, and control the sidebar on
              smaller screens.
            </li>
            <li>
              Use the <strong>time-range selector</strong> (7D, 1M, 3M, 6M, 1Y, All) on the dashboard to change the
              period for charts and insights.
            </li>
          </ul>

          <h3 className="mt-4 text-base font-semibold text-foreground">Quick-start checklist</h3>
          <ul>
            <li>Add or confirm your primary business location in Locations.</li>
            <li>Add your main products in Inventory, including cost and selling price.</li>
            <li>Log any recent sales using Manual Entry or confirm they are synced from your POS.</li>
            <li>Record key expenses so your profit and cash flow are accurate.</li>
            <li>Open Reports and check your first monthly Business Health Score.</li>
          </ul>

          <h2 className="mt-8 text-lg font-semibold text-foreground">4. Core sections of the app</h2>

          <h3 className="mt-4 text-base font-semibold text-foreground flex items-center gap-2">
            <BarChart3 className="h-4 w-4" /> BI Analyst (Dashboard)
          </h3>
          <p>
            The BI Analyst page is your main dashboard. It shows a 7-day health snapshot, AI insights, revenue trends,
            and financial KPIs. The snapshot uses metrics such as velocity, margin, turnover, and risk to help you see
            whether the business is improving or under pressure.
          </p>
          <p>
            Use the time range (7D, 1M, 3M, 6M, 1Y, All) to adjust the period. For long-term analysis and official
            monthly scores, use the Reports section.
          </p>

          <h3 className="mt-4 text-base font-semibold text-foreground flex items-center gap-2">
            <Package className="h-4 w-4" /> Inventory
          </h3>
          <p>
            Inventory is where you manage stock levels, add products, and set minimum thresholds. Each product can have
            a buying price, selling price, category, and branch assignment.
          </p>
          <ul>
            <li>Use minimum thresholds to mark items as low stock.</li>
            <li>
              Low-stock items appear with alerts so you can reorder before you run out, and may also drive AI tips and
              notifications.
            </li>
            <li>
              If you have multiple branches, you can filter inventory by location and move stock between branches using
              transfer options where available.
            </li>
          </ul>

          <h3 className="mt-4 text-base font-semibold text-foreground flex items-center gap-2">
            <MapPin className="h-4 w-4" /> Locations
          </h3>
          <p>
            Locations represent your branches or shops. Plan limits apply:{" "}
            <strong>Pro Solo</strong> supports one location; <strong>Pro Growth</strong> supports up to three
            locations.
          </p>
          <ul>
            <li>Add branches on the Locations page.</li>
            <li>In Inventory, assign products to specific locations and filter by branch.</li>
            <li>
              The dashboard shows each location’s item count when you have more than one, and reports can reflect
              multi-branch performance.
            </li>
            <li>
              For a full summary of what Pro Growth includes, see the &quot;How Pro Growth works&quot; section on the
              Locations page.
            </li>
          </ul>

          <h3 className="mt-4 text-base font-semibold text-foreground flex items-center gap-2">
            <BarChart3 className="h-4 w-4" /> Reports (Monthly Report Center)
          </h3>
          <p>
            The Reports section is your Monthly Report Center. Select a month to view your Profit &amp; Loss, cash
            flow, inventory health, top products, and the official Business Health Score for that month.
          </p>
          <ul>
            <li>Choose a month to see performance for that period and comparisons to previous months.</li>
            <li>
              Review sections such as P&amp;L, cash flow, inventory health, and top products to understand what is
              driving your results.
            </li>
            <li>Export reports to PDF for sharing with partners, lenders, or your accountant.</li>
          </ul>

          <h3 className="mt-4 text-base font-semibold text-foreground flex items-center gap-2">
            <FileText className="h-4 w-4" /> Manual Entry
          </h3>
          <p>
            Manual Entry lets you record sales directly in the app when needed. This is useful for quick logging, for
            example during outages, or when you are not using a connected POS system.
          </p>
          <ul>
            <li>Enter items sold, quantities, and amounts.</li>
            <li>Manual entries flow into your Master Ledger, reports, and AI insights.</li>
          </ul>

          <h3 className="mt-4 text-base font-semibold text-foreground flex items-center gap-2">
            <BookOpen className="h-4 w-4" /> Master Ledger
          </h3>
          <p>
            The Master Ledger is a complete list of sales transactions. Use it for detailed reviews, reconciliations,
            and audits.
          </p>
          <ul>
            <li>Search and filter by date, item, or amount.</li>
            <li>Use it to confirm that manual entries and synced sales are correctly captured.</li>
          </ul>

          <h3 className="mt-4 text-base font-semibold text-foreground flex items-center gap-2">
            <TrendingDown className="h-4 w-4" /> Expenses
          </h3>
          <p>
            Use the Expenses section to log and categorise business expenses. This information feeds into your P&amp;L
            and cash flow views.
          </p>
          <ul>
            <li>Add expenses with amount, date, and category.</li>
            <li>Use consistent categories so you can see which costs are growing or shrinking over time.</li>
          </ul>

          <h3 className="mt-4 text-base font-semibold text-foreground flex items-center gap-2">
            <Users className="h-4 w-4" /> Team
          </h3>
          <p>
            The Team section lets admins invite team members and manage their roles. This helps you give staff access to
            what they need without exposing sensitive information.
          </p>
          <ul>
            <li>Invite team members using their email address.</li>
            <li>
              Employees can be allowed to use areas such as Live Stock, Manual Entry, Sales Log, and Expenses according
              to your permissions model.
            </li>
          </ul>

          <h2 className="mt-8 text-lg font-semibold text-foreground flex items-center gap-2">
            <Settings className="h-5 w-5" /> 5. Settings &amp; customization
          </h2>
          <p>
            Use Settings to manage your business profile, billing, notifications, team, permissions, security,
            appearance, support links, and legal information. Settings are available from the sidebar and are designed
            to work well on both desktop and mobile.
          </p>

          <h3 className="mt-4 text-base font-semibold text-foreground">App Appearance</h3>
          <p>
            Under <strong>Settings → App Appearance</strong> you can choose the theme: <strong>Light</strong>,{" "}
            <strong>Dark</strong>, or <strong>System</strong> (match your device). Your choice is saved and applied
            across the app immediately. Theme can only be changed here, not from the main dashboard.
          </p>

          <h3 className="mt-4 text-base font-semibold text-foreground flex items-center gap-2">
            <Shield className="h-4 w-4" /> Security
          </h3>
          <p>
            In <strong>Settings → Security</strong> you can enable two-factor authentication (2FA) and manage session
            security. If you ever suspect suspicious activity, revoke all sessions and change your password from this
            section.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">6. AI daily tips &amp; insights</h2>
          <p>
            ReviewBoost Pro Manager uses AI to generate a short, practical tip for your business once per day for each
            active business. Tips are based on your real numbers, including sales, inventory, expenses, dead stock,
            slow movers, cash runway, and product mix.
          </p>
          <ul>
            <li>Each tip is at most two sentences and ends with a clear action you can take today.</li>
            <li>
              Tips only show when meaningful conditions are met (for example, dead stock rising, revenue dropping
              sharply, or cash runway becoming short).
            </li>
            <li>
              Free and Pro plans have access to different sets of conditions; Pro plans receive more advanced insights.
            </li>
          </ul>
          <p>
            Make it a habit to read your AI tip daily, decide whether you will act on it now or delegate it to a team
            member, and then review the related metrics in BI Analyst or Reports to see impact over time.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">7. Notifications &amp; alerts</h2>
          <p>
            The notification bell in the header shows important alerts for your business. Depending on your setup, this
            can include low-stock alerts, data or sync notifications, and other system messages that help you keep the
            business on track.
          </p>
          <ul>
            <li>Watch for low-stock alerts to reorder high-moving items before they run out.</li>
            <li>Use notifications together with AI tips and reports for a complete picture of what needs attention.</li>
          </ul>

          <h2 className="mt-8 text-lg font-semibold text-foreground">8. Plans, billing, and limits</h2>
          <p>
            ReviewBoost Pro Manager offers different plans to match your stage of growth. For example, Pro Solo focuses
            on a single location while Pro Growth supports up to three locations and additional features suitable for
            expanding businesses.
          </p>
          <p>
            You can review your current plan and billing details from Settings, where available in your app. All
            subscription payments are processed by Lenco. For details about pricing, billing cycles, and cancellation,
            see the{" "}
            <Link to="/terms" className="text-primary hover:underline">
              Terms of Service
            </Link>
            .
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">9. Support, safety, and data</h2>
          <p>
            If you need help, you can use the Support &amp; Safety section in Settings or contact us directly using the
            details below.
          </p>
          <ul>
            <li>
              <strong>Email support</strong>:{" "}
              <a href="mailto:support@reviewboost24.com" className="text-primary hover:underline">
                support@reviewboost24.com
              </a>
              .
            </li>
            <li>
              <strong>Support hours</strong>: Monday–Friday, 08:00–17:00 CAT. We aim to respond as quickly as possible
              during business hours.
            </li>
            <li>
              <strong>Security</strong>: If you see suspicious activity, revoke sessions and change your password in
              Settings → Security, then contact support.
            </li>
          </ul>
          <p>
            For privacy and legal information, please review the{" "}
            <Link to="/privacy" className="text-primary hover:underline">
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link to="/terms" className="text-primary hover:underline">
              Terms of Service
            </Link>
            .
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">10. Troubleshooting &amp; FAQ</h2>
          <h3 className="mt-4 text-base font-semibold text-foreground">My reports look empty or wrong</h3>
          <ul>
            <li>Check that you selected the correct month in Reports.</li>
            <li>Confirm that sales and expenses have been entered or synced for that period.</li>
            <li>Verify that you are looking at the correct location if you have multiple branches.</li>
            <li>If the problem continues, contact support with the month and location you are reviewing.</li>
          </ul>

          <h3 className="mt-4 text-base font-semibold text-foreground">I cannot find a product or transaction</h3>
          <ul>
            <li>Use search and filters in Inventory or the Master Ledger.</li>
            <li>Confirm that the item is assigned to the correct location.</li>
            <li>Check whether the sale was recorded on a different date than expected.</li>
          </ul>

          <h3 className="mt-4 text-base font-semibold text-foreground">I am not receiving daily AI tips</h3>
          <ul>
            <li>Make sure there is recent data (sales, inventory, and expenses) for your business.</li>
            <li>Check that you are on a plan that includes AI tips.</li>
            <li>If tips still do not appear after several days, contact support so we can investigate.</li>
          </ul>

          <h3 className="mt-4 text-base font-semibold text-foreground">I am having trouble logging in</h3>
          <ul>
            <li>Confirm that you are using the same Google account you used to sign up.</li>
            <li>Check your internet connection and try again.</li>
            <li>If you continue to have issues, reach out to support with a screenshot of the error (if possible).</li>
          </ul>

          <h2 className="mt-8 text-lg font-semibold text-foreground">11. Glossary</h2>
          <ul>
            <li>
              <strong>Business Health Score</strong>: A combined score that reflects revenue, profitability, inventory
              health, and risk for a selected month.
            </li>
            <li>
              <strong>Dead stock</strong>: Inventory that is not selling for a long time and is tying up your cash.
            </li>
            <li>
              <strong>Cash runway</strong>: An estimate of how many days you can continue to operate based on your
              current expenses and available cash.
            </li>
            <li>
              <strong>Slow movers</strong>: Products that sell, but at a low speed compared to the rest of your
              inventory.
            </li>
            <li>
              <strong>Branch / Location</strong>: A physical shop or operating location in your business.
            </li>
            <li>
              <strong>Pro Solo / Pro Growth</strong>: Subscription plans with different limits and capabilities, such as
              the number of supported locations.
            </li>
          </ul>
        </article>
      </div>
    </div>
  );
}
